import VueRouter, { RouteConfig } from 'vue-router';
import SystemError from '@views/error/system';
import { HEADER_TYPE } from '@berlin-front/common/components/clusters/ModalHeader/composable';
import Home from '@views/home';
import { SystemErrorViewContext } from '@views/error/system/composable';
import { onRouteChange } from '@composables/useTraceRoutes';
import { ROUTE_NAME } from './route-name';

// サービスタイプ別のrouteをインポート
import atoneRoute from './atone-route';
import atobaraiRoute from './atobarai-route';
import atoneLiteRoute from './atone-lite-route';
import atoneAuthRoute from './atone-auth-route';
import atoneLiteAuthRoute from './atone-lite-auth-route';
import atoneSplitRoute from './atone-split-route';

/*

## ルーターの役割と規則について

### 基本的にはサービスタイプ別にルートを切る。

ほぼ同じ内容でも、routeは別に切るようにすること。

- システムエラー、ショップエラー、メンテナンスエラーが特別なのは、画面ロジックではなく、APIのレスポンスや例外にフックして遷移するため。
- 共通フックにサービスタイプへの依存を入れたくないため、遷移先URLにサービスタイプが入らないという側面もある。（サービスタイプとurlのマップファイルを作っても良かったけど。）

### ルーターの役割

当プロジェクトは性質上、同じ見た目で異なるAPIにバインドするケースが多い。

- 同じ画面コンポーネントを複数のルートに当てる
- 同じ画面コンポーネントでも、使用するAPIなど画面ロジックの差し替えが発生する

といった要件が多い。これを実現するための仕掛けとしてrouterによってViewに対する実装ロジックを差し替える、という手法を使っている。
別な言い方をすると、routerを使用してDIを行っている、という説明になる。

ViewとModelの紐付けを行う基本的なルールは以下に示すようなもので、実装はこれを厳守する。

- Viewはvueファイルとして提供する。
- インターフェイスはContextProviderという形で提供する。
- ロジックは差し替え可能なものとして、ContextProviderで指定されたインターフェイスを持つ

以下サンプル。

```typescript
// route定義サンプル
{
  name: 'route-name',
  path: '/atone/sample-route'
  components: {
    // 画面コンポーネントを指定する。
    default: ViewComponent,
    // 画面にロジックを提供するプロバイダーを指定する。
    contextProvider: LogicContext.provider
  },
  props: {
    // これがLogic.providerのvalue値として設定される。通常はこれが一つのcomposableを提供すると考えて良い。
    // logicContextValueを差し替えればロジックが変わる。
    contextProvider: logicContextValue
  },
}

// ViewComponentサンプル
export default defineComponent({
  ...
  setup() {
    // propsで指定された実装を取得する。先に述べたようにcomposableで（useXxxxのような）実装を提供する。
    // contextから流し込めるものはオブジェクトに限定される実装のため、一つのcomposableを提供する場合も
    // このような感じになる。
    const { useXxxx } = useContext(LogicContext);
    
    // ここで受け取るのは画面を表示するためのstateやイベントハンドラ。
    // vueファイル自体には基本的に実装を書かない。全ては提供される `useXxxx` 内に実装するようにする。
    const { value1, eventHandler1, .... } = useXxxx();

    // vueファイル内にはロジックをほぼかかず、stateやハンドラは直接templateに渡すようにするのが望ましい。
    return {
      value1,
      eventHandler1,
    };
  }
})
```

*/

const routes: RouteConfig[] = [
  // サービスタイプに依存しない共通ルート
  {
    // 初期画面（何も表示しない空の画面。）
    // ローディングすら出せないタイミングがあるため用意されている。
    path: '/',
    name: ROUTE_NAME.HOME,
    component: Home,
  },
  {
    // エラー画面。エラー画面のみ共通ルートに定義される。
    path: '/error/:errorType(system|shop|maintainance)',
    name: ROUTE_NAME.ERROR_VIEW,
    meta: {
      headerType: HEADER_TYPE.SHOP_PRICE,
    },
    components: {
      default: SystemError,
      contextProvider: SystemErrorViewContext.provider,
    },
    props: {
      default: (route) => ({ errorType: route.params.errorType }),
    },
  },

  // 01. Atoneのルート定義
  atoneRoute,

  // 02. NP後払いのルート定義
  atobaraiRoute,

  // 03. ATONE_LITEのルート
  atoneLiteRoute,

  // 05. ATONEAUTHのルート
  atoneAuthRoute,

  // 06. ATONE_LITE_AUTHのルート
  atoneLiteAuthRoute,

  // 07. ATONE_SPLITのルート
  atoneSplitRoute,
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.VUE_APP_BASE_URL,
  routes,
  linkActiveClass: '',
  linkExactActiveClass: '',
});

router.afterEach((to) => {
  // traceRoutesの記録をとる。
  onRouteChange(to);
});
// router.afterEach((to) => {
//   console.log(to.name);
// });

export default router;
